import BaseResource from '@/lib/data/resource/BaseResource'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'

export default class RatesResource extends BaseResource {
  constructor (payload = {}) {
    super(payload)

    const p = payload || {}

    /* ----------------- Base Currency ----------------- */
    this.baseCurrency = {
      symbol           : p.baseCurrency?.symbol ?? '',
      displayName      : p.baseCurrency?.displayName ?? '',
      displaySymbol    : p.baseCurrency?.displaySymbol ?? '',
      name             : p.baseCurrency?.name ?? '',
      supportedFeatures: p.baseCurrency?.supportedFeatures ?? []
    }

    /* ----------------- Bank ----------------- */
    this.bank = {
      repricingSpeed: {
        loans: {
          current : p.bank?.repricingSpeed?.loans?.currentSpeed ?? 0,
          lastYear: p.bank?.repricingSpeed?.loans?.lastYearSpeed ?? 0
        },
        deposits: {
          current : p.bank?.repricingSpeed?.deposits?.currentSpeed ?? 0,
          lastYear: p.bank?.repricingSpeed?.deposits?.lastYearSpeed ?? 0
        }
      },
      franchiseStrength: {
        min            : p.bank?.franchiseStrength?.min ?? 0,
        max            : p.bank?.franchiseStrength?.max ?? 0,
        currentQuarter : p.bank?.franchiseStrength?.currentQuarterStrength ?? 0,
        twelveMonthsAgo: p.bank?.franchiseStrength?.twelveMonthsAgoStrength ?? 0
      },
      netInterestIncomeSensitivity: {
        sofrFactor : p.bank?.netInterestIncomeSensitivity?.sofrFactor ?? 0,
        slopeFactor: p.bank?.netInterestIncomeSensitivity?.slopeFactor ?? 0,
        assetLabel : p.bank?.netInterestIncomeSensitivity?.assetLabel ?? '',
        sofr       : {
          rates  : p.bank?.netInterestIncomeSensitivity?.sofr?.rates ?? [],
          amounts: p.bank?.netInterestIncomeSensitivity?.sofr?.amounts ?? [],
          samples: {
            rates  : p.bank?.netInterestIncomeSensitivity?.sofr?.ratesSamples ?? [],
            amounts: p.bank?.netInterestIncomeSensitivity?.sofr?.amountsSamples ?? []
          }
        },
        slope: {
          rates  : p.bank?.netInterestIncomeSensitivity?.slope?.rates ?? [],
          amounts: p.bank?.netInterestIncomeSensitivity?.slope?.amounts ?? [],
          samples: {
            rates  : p.bank?.netInterestIncomeSensitivity?.slope?.ratesSamples ?? [],
            amounts: p.bank?.netInterestIncomeSensitivity?.slope?.amountsSamples ?? []
          }
        }
      },
      marginSpreadSensitivity: {
        spreadConstant: p.bank?.marginSpreadSensitivity?.spreadConstant ?? 0,
        sofrFactor    : p.bank?.marginSpreadSensitivity?.sofrFactor ?? 0,
        dates         : p.bank?.marginSpreadSensitivity?.dates ?? [],
        marginSpread  : p.bank?.marginSpreadSensitivity?.marginSpread ?? [],
        assetLabel    : p.bank?.marginSpreadSensitivity?.assetLabel ?? ''
      }
    }

    // ------- Peers -------
    this.peers = {
      repricingSpeed   : p.peers?.repricingSpeed ?? {},
      franchiseStrength: p.peers?.franchiseStrength ?? {},
      niiSensitivity   : p.peers?.niiSensitivity ?? {},
      marginSpread     : p.peers?.marginSpread ?? {}
    }
  }

  /* ----------------- Helpers ----------------- */
  static _isNum (n) { return typeof n === 'number' && !Number.isNaN(n) }
  static _sum (arr = []) { return arr.reduce((a, b) => a + (this._isNum(b) ? b : 0), 0) }
  static _avg (arr = []) {
    const nums = arr.filter(this._isNum)
    return nums.length ? this._sum(nums) / nums.length : 0
  }

  static _pctChange (curr, prev) {
    if (!this._isNum(curr) || !this._isNum(prev) || prev === 0) return 0
    return ((curr - prev) / Math.abs(prev)) * 100
  }

  static _movingAvg (arr = [], window = 3) {
    if (!arr.length || window <= 1) return [...arr]
    const out = []
    for (let i = 0; i < arr.length; i++) {
      const start = Math.max(0, i - window + 1)
      const slice = arr.slice(start, i + 1)
      out.push(this._avg(slice))
    }
    return out
  }

  /* ----------------- Repricing ----------------- */
  get loanRepricingYoYDelta () {
    const { current, lastYear } = this.bank.repricingSpeed.loans
    return current - lastYear
  }

  get loanRepricingYoYPct () {
    const { current, lastYear } = this.bank.repricingSpeed.loans
    return RatesResource._pctChange(current, lastYear)
  }

  get depositRepricingYoYDelta () {
    const { current, lastYear } = this.bank.repricingSpeed.deposits
    return current - lastYear
  }

  get depositRepricingYoYPct () {
    const { current, lastYear } = this.bank.repricingSpeed.deposits
    return RatesResource._pctChange(current, lastYear)
  }

  get repricingLoanVsDepositGap () {
    return (this.bank.repricingSpeed.loans.current ?? 0) - (this.bank.repricingSpeed.deposits.current ?? 0)
  }

  get formattedLoanRepricing () {
    return toNumberFormatNotationShort(this.bank.repricingSpeed.loans.current ?? 0)
  }

  get formattedDepositRepricing () {
    return toNumberFormatNotationShort(this.bank.repricingSpeed.deposits.current ?? 0)
  }

  // =========================
  // NII Sensitivity helpers
  // =========================
  get niiExposureSummary () {
    const { sofrFactor, slopeFactor } = this.bank.netInterestIncomeSensitivity
    const total = (sofrFactor || 0) + (slopeFactor || 0)
    const sofrWeight = total ? (sofrFactor / total) : 0
    const slopeWeight = total ? (slopeFactor / total) : 0

    return {
      sofrFactor,
      slopeFactor,
      total,
      weights: {
        sofr : sofrWeight,
        slope: slopeWeight
      }
    }
  }

  /* ----------------- NII Sensitivity ----------------- */
  curveSeries (kind = 'sofr', { samples = false } = {}) {
    const block = this.bank.netInterestIncomeSensitivity?.[kind] ?? {}
    if (!block) return []

    const rates = samples ? (block.samples?.rates ?? []) : (block.rates ?? [])
    const amts  = samples ? (block.samples?.amounts ?? []) : (block.amounts ?? [])

    const len = Math.min(rates.length, amts.length)
    const out = []
    for (let i = 0; i < len; i++) out.push({ x: rates[i], y: amts[i] })
    return out
  }

  /* ----------------- NII Sensitivity ----------------- */
  curveForRecharts (kind = 'sofr', { samples = false } = {}) {
    return this.curveSeries(kind, { samples }).map(pt => ({ rate: pt.x, amount: pt.y }))
  }

  // =========================
  // Peer helpers (averages, ranks, deltas)
  // =========================

  /* ----------------- Repricing ----------------- */
  peersAvgRepricing () {
    const data = this.peers?.repricingSpeed?.peersData ?? []
    const peersOnly = data.filter(d => !d.bank)
    const avgDepositSpeed = RatesResource._avg(peersOnly.map(p => p.depositSpeed))
    // Loan speed approximated as depositSpeed / ratio
    const loanSpeeds = peersOnly.map(p => {
      const ratio = (p.depositLoanSpeedRatio || 0)
      return ratio ? (p.depositSpeed / ratio) : 0
    })
    const avgLoanSpeed = RatesResource._avg(loanSpeeds)
    return { avgDepositSpeed, avgLoanSpeed }
  }

  /* ----------------- Repricing ----------------- */
  bankVsPeersRepricing () {
    const bankRow = (this.peers?.repricingSpeed?.peersData ?? []).find(d => d.bank)
    const bankDeposit = bankRow?.depositSpeed ?? this.bank.repricingSpeed.deposits.current
    const bankLoan = (() => {
      const ratio = bankRow?.depositLoanSpeedRatio
      return (ratio ? bankDeposit / ratio : this.bank.repricingSpeed.loans.current)
    })()

    const { avgDepositSpeed, avgLoanSpeed } = this.peersAvgRepricing()

    return {
      bank    : { deposit: bankDeposit, loan: bankLoan },
      peersAvg: { deposit: avgDepositSpeed, loan: avgLoanSpeed },
      deltas  : {
        deposit: bankDeposit - avgDepositSpeed,
        loan   : bankLoan - avgLoanSpeed
      },
      pct: {
        deposit: RatesResource._pctChange(bankDeposit, avgDepositSpeed),
        loan   : RatesResource._pctChange(bankLoan, avgLoanSpeed)
      }
    }
  }

  /* ----------------- Franchise ----------------- */
  bankFranchiseRank () {
    const list = this.peers?.franchiseStrength?.perPeerFranchiseStrength ?? []
    if (!list.length) return { rank: 0, total: 0 }
    const sorted = [...list].sort((a, b) => (b.franchiseStrength ?? 0) - (a.franchiseStrength ?? 0))
    const idx = sorted.findIndex(x => x.bank) // the "Bank" row
    return { rank: idx >= 0 ? idx + 1 : 0, total: sorted.length }
  }

  /* ----------------- NII Sensitivity ----------------- */
  peersAvgNIIFactors () {
    const list = this.peers?.niiSensitivity?.perPeerNIISensitivity ?? []
    const peersOnly = list.filter(x => !x.bank)
    return {
      avgSofr : RatesResource._avg(peersOnly.map(x => x.sofrFactor)),
      avgSlope: RatesResource._avg(peersOnly.map(x => x.slopeFactor))
    }
  }

  /* ----------------- NII Sensitivity ----------------- */
  bankVsPeersNII () {
    const list = this.peers?.niiSensitivity?.perPeerNIISensitivity ?? []
    const bank = list.find(x => x.bank)
    const bankSofr = bank?.sofrFactor ?? this.bank.netInterestIncomeSensitivity.sofrFactor
    const bankSlope = bank?.slopeFactor ?? this.bank.netInterestIncomeSensitivity.slopeFactor
    const { avgSofr, avgSlope } = this.peersAvgNIIFactors()
    return {
      bank    : { sofr: bankSofr, slope: bankSlope },
      peersAvg: { sofr: avgSofr, slope: avgSlope },
      deltas  : { sofr: bankSofr - avgSofr, slope: bankSlope - avgSlope },
      pct     : { sofr: RatesResource._pctChange(bankSofr, avgSofr), slope: RatesResource._pctChange(bankSlope, avgSlope) }
    }
  }

  /* ----------------- Helpers ----------------- */
  static _rankList (rows = [], valueKey = 'value', desc = true) {
    const s = [...rows].sort((a, b) => (desc ? (b[valueKey] - a[valueKey]) : (a[valueKey] - b[valueKey])))
    return s.map((row, i) => ({ ...row, rank: i + 1 }))
  }

  // =========================
  // Chart-ready builders
  // =========================

  /* ----------------- Repricing ----------------- */
  repricingSummary () {
    return {
      loans: {
        current         : this.bank.repricingSpeed.loans.current,
        lastYear        : this.bank.repricingSpeed.loans.lastYear,
        delta           : this.loanRepricingYoYDelta,
        pct             : this.loanRepricingYoYPct,
        formattedCurrent: toNumberFormatNotationShort(this.bank.repricingSpeed.loans.current ?? 0)
      },
      deposits: {
        current         : this.bank.repricingSpeed.deposits.current,
        lastYear        : this.bank.repricingSpeed.deposits.lastYear,
        delta           : this.depositRepricingYoYDelta,
        pct             : this.depositRepricingYoYPct,
        formattedCurrent: toNumberFormatNotationShort(this.bank.repricingSpeed.deposits.current ?? 0)
      },
      gap: this.repricingLoanVsDepositGap
    }
  }

  /* ----------------- NII Sensitivity ----------------- */
  niiWeightsPie () {
    const s = this.niiExposureSummary()
    return [
      { name: 'SOFR', value: s.weights.sofr },
      { name: 'Slope', value: s.weights.slope }
    ]
  }

  /* ----------------- Formatting ----------------- */
  fmtPct (n, digits = 1) {
    if (!RatesResource._isNum(n)) return '0%'
    return `${n.toFixed(digits)}%`
  }

  fmtNumShort (n) {
    return toNumberFormatNotationShort(n ?? 0)
  }

  static fromApiResponse (payload) {
    return new RatesResource(payload)
  }
}
